import { useCallback, useRef, useState } from 'react';

type GridPosition = { row: number; col: number };
type PositionMap = Record<string, GridPosition>;

type UseHomepageGridDragDropParams = {
  spanMap: Record<string, number>;
  gridCols: number;
  setPositions: React.Dispatch<React.SetStateAction<PositionMap>>;
  onPositionChange?: (positions: PositionMap) => void;
  savePositions: (positions: PositionMap) => void;
  scrollContainerRef: React.RefObject<HTMLElement | null>;
  dragOutlineColor?: string;
};

const AUTO_SCROLL_THRESHOLD_PX = 80;
const AUTO_SCROLL_STEP_PX = 18;

export const useHomepageGridDragDrop = ({
  spanMap,
  gridCols,
  setPositions,
  onPositionChange,
  savePositions,
  scrollContainerRef,
  dragOutlineColor = '#1976d2',
}: UseHomepageGridDragDropParams) => {
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const lastDragYRef = useRef<number | null>(null);

  const runAutoScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    const pointerY = lastDragYRef.current;
    if (!container || pointerY == null) return;

    const rect = container.getBoundingClientRect();
    if (pointerY < rect.top + AUTO_SCROLL_THRESHOLD_PX) {
      container.scrollTop -= AUTO_SCROLL_STEP_PX;
    } else if (pointerY > rect.bottom - AUTO_SCROLL_THRESHOLD_PX) {
      container.scrollTop += AUTO_SCROLL_STEP_PX;
    }
  }, [scrollContainerRef]);

  const handleOnDragStart = useCallback((e: React.DragEvent, columnId: string) => {
    e.dataTransfer.setData('columnId', columnId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggingId(columnId);
  }, []);

  const handleOnDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';

      lastDragYRef.current = e.clientY;
      runAutoScroll();

      const target = (e.target as HTMLElement).closest('[data-column-id]');
      if (target) {
        setDragOverId((target as HTMLElement).dataset.columnId || null);
      }
    },
    [runAutoScroll],
  );

  const handleOnDrop = useCallback(
    (e: React.DragEvent, targetId: string) => {
      e.preventDefault();
      setDragOverId(null);

      const sourceId = e.dataTransfer.getData('columnId');
      if (!sourceId || sourceId === targetId) return;

      setPositions((prev) => {
        const sourcePos = prev[sourceId];
        const targetPos = prev[targetId];
        if (!sourcePos || !targetPos) return prev;

        const sourceSpan = spanMap[sourceId] ?? 1;
        const targetSpan = spanMap[targetId] ?? 1;

        if (sourceSpan === 1 && targetSpan === 1) {
          const next = { ...prev, [sourceId]: targetPos, [targetId]: sourcePos };
          savePositions(next);
          onPositionChange?.(next);
          return next;
        }

        if (sourcePos.row === targetPos.row && (sourceSpan > 1 || targetSpan > 1)) {
          const next = { ...prev };
          const wideId = sourceSpan > 1 ? sourceId : targetId;
          const singleId = sourceSpan > 1 ? targetId : sourceId;
          const wideSpan = spanMap[wideId] ?? 1;

          const singleCol = Math.max(1, gridCols - ((spanMap[singleId] ?? 1) - 1));
          next[wideId] = { row: sourcePos.row, col: 1 };
          const desiredSingleCol = Math.max(wideSpan + 1, singleCol);
          next[singleId] = {
            row: sourcePos.row,
            col: Math.min(gridCols, desiredSingleCol),
          };

          savePositions(next);
          onPositionChange?.(next);
          return next;
        }

        const next = { ...prev };

        const wideId = sourceSpan > 1 ? sourceId : targetId;
        const singleId = sourceSpan > 1 ? targetId : sourceId;
        const widePos = prev[wideId];
        const singlePos = prev[singleId];
        const wideSpan = spanMap[wideId] ?? 1;

        next[wideId] = { row: singlePos.row, col: 1 };
        next[singleId] = { row: widePos.row, col: widePos.col };

        const wideOccupies = new Set<number>();
        for (let c = 1; c <= wideSpan; c++) wideOccupies.add(c);

        const collateral: string[] = [];
        for (const [id, pos] of Object.entries(prev)) {
          if (id === sourceId || id === targetId) continue;
          if (pos.row === singlePos.row && wideOccupies.has(pos.col)) {
            collateral.push(id);
          }
        }

        const usedInOldRow = new Set<number>();
        const singleNewSpan = spanMap[singleId] ?? 1;
        for (let c = next[singleId].col; c < next[singleId].col + singleNewSpan; c++) {
          usedInOldRow.add(c);
        }
        for (const [id, pos] of Object.entries(prev)) {
          if (id === sourceId || id === targetId) continue;
          if (collateral.includes(id)) continue;
          if (pos.row === widePos.row) usedInOldRow.add(pos.col);
        }

        const maxExistingRow = Math.max(...Object.values(prev).map((p) => p.row), 1);
        let nextNewRow = maxExistingRow + 1;

        for (const id of collateral) {
          let placed = false;
          for (let c = 1; c <= gridCols; c++) {
            if (!usedInOldRow.has(c)) {
              next[id] = { row: widePos.row, col: c };
              usedInOldRow.add(c);
              placed = true;
              break;
            }
          }
          if (!placed) {
            next[id] = { row: nextNewRow, col: prev[id].col };
            nextNewRow++;
          }
        }

        savePositions(next);
        onPositionChange?.(next);
        return next;
      });
    },
    [gridCols, onPositionChange, savePositions, setPositions, spanMap],
  );

  const handleDragEnd = useCallback(() => {
    setDragOverId(null);
    setDraggingId(null);
    lastDragYRef.current = null;
  }, []);

  const getDropZoneProps = useCallback(
    (columnId: string) => ({
      'data-column-id': columnId,
      onDragOver: handleOnDragOver,
      onDrop: (e: React.DragEvent) => handleOnDrop(e, columnId),
      onDragEnd: handleDragEnd,
    }),
    [handleOnDragOver, handleOnDrop, handleDragEnd],
  );

  const dragOverSx = useCallback(
    (columnId: string) =>
      dragOverId === columnId
        ? {
            outline: `2px dashed ${dragOutlineColor}`,
            outlineOffset: '-2px',
            borderRadius: '12px',
            opacity: 0.7,
          }
        : draggingId === columnId
          ? { opacity: 0.5 }
          : {},
    [dragOutlineColor, dragOverId, draggingId],
  );

  return {
    draggingId,
    handleOnDragStart,
    getDropZoneProps,
    dragOverSx,
  };
};
